import type { Memo, Summary } from '@memori-web/shared';
import { renderMemoList } from './memo-list.js';
import { renderAiSummary } from './ai-summary.js';

export interface DayDetailSheetOptions {
  memos: Memo[]; // 全メモ (sheet 内で日切替するために全部渡す)
  initialDate: string;
  todayDate: string;
  getCachedSummary: (date: string) => Summary | null;
  onGenerateSummary: (date: string) => Promise<Summary>;
}

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

function formatDate(ymd: string): string {
  const d = new Date(ymd);
  return `${d.getMonth() + 1}/${d.getDate()} (${WEEKDAYS[d.getDay()]})`;
}
function shiftDate(ymd: string, days: number): string {
  const d = new Date(ymd);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function showDayDetailSheet(opts: DayDetailSheetOptions): Promise<void> {
  return new Promise((resolve) => {
    let currentDate = opts.initialDate;

    const root = document.createElement('div');
    root.className = 'day-sheet';
    root.innerHTML = `
      <div class="day-sheet__backdrop"></div>
      <div class="day-sheet__panel" role="dialog" aria-label="選択日の詳細">
        <header class="day-sheet__header">
          <button type="button" class="day-sheet__nav-btn" data-dir="-1" aria-label="前の日">‹</button>
          <div class="day-sheet__title">
            <span class="day-sheet__date" id="day-sheet-date"></span>
            <span class="day-sheet__count" id="day-sheet-count"></span>
          </div>
          <button type="button" class="day-sheet__nav-btn" data-dir="1" aria-label="次の日">›</button>
        </header>
        <div class="day-sheet__body">
          <div id="day-sheet-ai"></div>
          <div id="day-sheet-memos"></div>
        </div>
      </div>
    `;
    document.body.appendChild(root);
    requestAnimationFrame(() => root.classList.add('is-open'));

    const dateEl = root.querySelector<HTMLSpanElement>('#day-sheet-date')!;
    const countEl = root.querySelector<HTMLSpanElement>('#day-sheet-count')!;
    const memosEl = root.querySelector<HTMLDivElement>('#day-sheet-memos')!;
    const aiEl = root.querySelector<HTMLDivElement>('#day-sheet-ai')!;

    const refresh = () => {
      const dayMemos = opts.memos.filter(m => m.date === currentDate && !m.deletedAt);
      dateEl.textContent = formatDate(currentDate) + (currentDate === opts.todayDate ? ' (今日)' : '');
      countEl.textContent = `${dayMemos.length} 件`;
      renderMemoList(memosEl, dayMemos, {});
      renderAiSummary(aiEl, currentDate, opts.getCachedSummary(currentDate), {
        onGenerate: opts.onGenerateSummary,
      });
    };
    refresh();

    const close = () => {
      root.classList.remove('is-open');
      setTimeout(() => { root.remove(); resolve(); }, 200);
    };
    root.querySelector('.day-sheet__backdrop')!.addEventListener('click', close);
    root.querySelectorAll<HTMLButtonElement>('[data-dir]').forEach(btn => {
      btn.addEventListener('click', () => {
        const dir = parseInt(btn.dataset.dir!, 10);
        currentDate = shiftDate(currentDate, dir);
        refresh();
      });
    });
  });
}
